import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getProfessorById, Professor } from '../../services/professorService';
import { sendEmail, checkGmailStatus, EmailProvider, generatePersonalizedEmail } from '../../services/emailService';
import './ProfessorDetail.css';

interface ProfessorDetailProps {
  professor?: Professor;
}

const ProfessorDetail: React.FC<ProfessorDetailProps> = ({ professor: propProfessor }) => {
  const { id } = useParams<{ id: string }>();
  const [professor, setProfessor] = useState<Professor | null>(propProfessor || null);
  const [loading, setLoading] = useState<boolean>(!propProfessor);
  const [error, setError] = useState<string | null>(null);
  const [emailDraft, setEmailDraft] = useState<string>('');
  const [copySuccess, setCopySuccess] = useState<boolean>(false);
  const [sendingEmail, setSendingEmail] = useState<boolean>(false);
  const [generatingEmail, setGeneratingEmail] = useState<boolean>(false);
  const [emailSent, setEmailSent] = useState<boolean>(false);
  const [gmailConnected, setGmailConnected] = useState<boolean>(false);
  const [gmailEmail, setGmailEmail] = useState<string>('');
  const [emailProvider, setEmailProvider] = useState<EmailProvider>('sendgrid');

  // Fetch professor data
  useEffect(() => {
    const fetchProfessor = async () => {
      if (!propProfessor && id) {
        try {
          setLoading(true);
          const data = await getProfessorById(parseInt(id));
          setProfessor(data);
        } catch (err) {
          console.error('Error fetching professor:', err);
          setError('Failed to load professor details. Please try again later.');
        } finally {
          setLoading(false);
        }
      }
    };

    fetchProfessor();
  }, [id, propProfessor]);

  // Generate personalized email
  const handleGenerateEmail = async () => {
    if (!professor) return;

    try {
      setGeneratingEmail(true);
      const userInfo = {
        name: 'Vidyuth Ashok Kumar',
        grade: 'junior',
        school: 'North Carolina School of Science and Mathematics',
        email: 'vidyuth.ak9@gmail.com',
        achievements: [
          'Student/Postdoc member of the New York Academy of Sciences (NYAS)',
          '3rd in State for Software Development (TSA)',
          'Qualified nationally for Data Science and Analytics',
          'Won the NCSAS Regional competition for Predictive Modeling'
        ]
      };

      const response = await generatePersonalizedEmail(
        professor.id,
        userInfo,
        'I am particularly interested in your work on ' + 
          (professor.research_areas?.[0] || 'your research area') + 
          ' and would appreciate the opportunity to learn more about your current projects.',
        professor.name,
        professor.research_summary || professor.research_areas?.join(', ') || 'your research'
      );

      if (response.content) {
        setEmailDraft(response.content);
      } else {
        throw new Error('Failed to generate email content');
      }
    } catch (err) {
      console.error('Error generating email:', err);
      setError('Failed to generate personalized email. Please try again later.');
    } finally {
      setGeneratingEmail(false);
    }
  };

  // Copy email to clipboard
  const handleCopyEmail = () => {
    if (!emailDraft) return;

    navigator.clipboard.writeText(emailDraft)
      .then(() => {
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      })
      .catch(err => {
        console.error('Failed to copy email:', err);
      });
  };

  // Check Gmail connection status
  useEffect(() => {
    const checkGmail = async () => {
      try {
        console.log('Checking Gmail status...');
        const status = await checkGmailStatus();
        console.log('Gmail status response:', status);
        setGmailConnected(status.connected);
        if (status.email) {
          setGmailEmail(status.email);
          console.log('Gmail email set to:', status.email);
        }
      } catch (err) {
        console.error('Error checking Gmail status:', err);
      }
    };
    
    // Check Gmail status immediately
    checkGmail();
    
    // Check for Gmail connection success from redirect
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('gmail_connected') === 'true') {
      setGmailConnected(true);
      const email = urlParams.get('gmail_email');
      if (email) {
        setGmailEmail(email);
        console.log('Gmail email set from URL params:', email);
      }
      // Remove query parameter
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    
    // Set up interval to check Gmail status every 3 seconds
    const intervalId = setInterval(checkGmail, 3000);
    
    return () => clearInterval(intervalId);
  }, []);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p>Loading professor details...</p>
      </div>
    );
  }

  if (error || !professor) {
    return (
      <div className="container py-5">
        <div className="alert alert-danger" role="alert">
          {error || 'Professor not found'}
        </div>
        <Link to="/professors" className="btn btn-primary">
          Back to Professors
        </Link>
      </div>
    );
  }

  return (
    <div className="professor-detail">
      <div className="container">
        <div className="row mb-4">
          <div className="col-md-8">
            <h1 className="mb-2">{professor.name || (professor.email ? professor.email.split('@')[0] : 'Professor')}</h1>
            <h5 className="text-muted">{professor.title || 'Professor'} at {professor.university || 'Unknown University'}</h5>
            <p>
              <span className="badge bg-secondary me-2">{professor.department || 'Unknown Department'}</span>
              {professor.research_areas && professor.research_areas.map((area, index) => (
                <span key={index} className="badge bg-primary me-2">{area}</span>
              ))}
            </p>
          </div>
          <div className="col-md-4 text-md-end">
            <a href={`mailto:${professor.email}`} className="btn btn-outline-primary me-2">
              <i className="bi bi-envelope"></i> {professor.email}
            </a>
            {professor.webpage && (
              <a href={professor.webpage} target="_blank" rel="noopener noreferrer" className="btn btn-outline-secondary">
                <i className="bi bi-globe"></i> Website
              </a>
            )}
          </div>
        </div>

        <div className="row">
          <div className="col-lg-8">
            <div className="card mb-4">
              <div className="card-header">
                <h3 className="card-title mb-0">Research Summary</h3>
              </div>
              <div className="card-body">
                {professor.research_summary ? (
                  <p>{professor.research_summary}</p>
                ) : (
                  <p className="text-muted">No research summary available.</p>
                )}
              </div>
            </div>

            <div className="card">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h3 className="card-title mb-0">Personalized Email</h3>
                <div>
                  <button 
                    className="btn btn-primary me-2" 
                    onClick={handleGenerateEmail}
                    disabled={generatingEmail}
                  >
                    {generatingEmail ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Generating...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-magic"></i> Generate Personalized Email
                        <p className="text-muted mt-2">
                          <small>Our AI will create a tailored email based on {professor.name.split(' ')[0]}'s research interests</small>
                        </p>
                      </>
                    )}
                  </button>
                </div>
              </div>
              
              <div className="card-body">
                {emailDraft ? (
                  <div className="email-template">
                    <div className="email-actions mb-3">
                      <button 
                        className="btn btn-sm btn-outline-secondary me-2" 
                        onClick={handleCopyEmail}
                        disabled={copySuccess}
                      >
                        {copySuccess ? 'Copied!' : 'Copy to Clipboard'}
                      </button>
                      
                      <div className="btn-group">
                        <button 
                          className={`btn btn-sm ${emailProvider === 'sendgrid' ? 'btn-primary' : 'btn-outline-primary'}`}
                          onClick={() => setEmailProvider('sendgrid')}
                        >
                          SendGrid
                        </button>
                        <button 
                          className={`btn btn-sm ${emailProvider === 'gmail' ? 'btn-primary' : 'btn-outline-primary'}`}
                          onClick={() => setEmailProvider('gmail')}
                          disabled={!gmailConnected}
                          title={!gmailConnected ? 'Connect Gmail in Settings first' : ''}
                        >
                          Gmail {gmailConnected && <span className="badge bg-success">Connected</span>}
                        </button>
                      </div>
                      
                      <button 
                        className="btn btn-sm btn-success ms-2" 
                        onClick={async () => {
                          if (professor) {
                            try {
                              setSendingEmail(true);
                              await sendEmail(
                                professor.id,
                                'Research Opportunity Inquiry',
                                emailDraft,
                                undefined, // user ID
                                emailProvider,
                                professor.email // recipient email
                              );
                              setEmailSent(true);
                              setTimeout(() => setEmailSent(false), 5000);
                            } catch (err) {
                              console.error('Error sending email:', err);
                            } finally {
                              setSendingEmail(false);
                            }
                          }
                        }}
                        disabled={sendingEmail || !emailDraft}
                      >
                        {sendingEmail ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Sending...
                          </>
                        ) : (
                          <>Send Email via {emailProvider === 'gmail' ? 'Gmail' : 'SendGrid'}</>
                        )}
                      </button>
                      
                      {emailSent && (
                        <div className="alert alert-success mt-3" role="alert">
                          Email sent successfully!
                        </div>
                      )}
                    </div>
                    
                    <div className="email-content" dangerouslySetInnerHTML={{ __html: emailDraft }}></div>
                  </div>
                ) : (
                  <p className="text-muted">Click "Generate Personalized Email" to create a tailored email for {professor.name}.</p>
                )}
              </div>
            </div>
          </div>
          
          <div className="col-lg-4">
            <div className="card mb-4">
              <div className="card-header">
                <h3 className="card-title mb-0">Contact Information</h3>
              </div>
              <div className="card-body">
                <p><strong>Email:</strong> {professor.email}</p>
                {professor.webpage && (
                  <p>
                    <strong>Website:</strong> <a href={professor.webpage} target="_blank" rel="noopener noreferrer">{professor.webpage}</a>
                  </p>
                )}
              </div>
            </div>
            
            <div className="card">
              <div className="card-header">
                <h3 className="card-title mb-0">University Information</h3>
              </div>
              <div className="card-body">
                <p><strong>University:</strong> {professor.university || 'Unknown University'}</p>
                <p><strong>Department:</strong> {professor.department || 'Unknown Department'}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfessorDetail;
